import type { fleetState, maintenance } from '@kanbios/api-client';

type Running = fleetState.components['schemas']['Running'];
type Container = Running['containers'][number];
type Site = Running['sites'][number];
type HostUnit = NonNullable<Running['units']>[number];
export type DeclarationDraft = maintenance.components['schemas']['DeclarationDraft'];
type ContainerRule = maintenance.components['schemas']['ContainerRule'];
type UnitRule = { kind: 'systemd' | 'cron'; prefix: string };

/**
 * D48 « Déclarer une application »: the first guess the portal makes from
 * what the agent reads on the server. A guess only: the person corrects every
 * line, and the backend checks the result (`POST …/applications/preview`).
 */

/** One line of the « Conteneurs » step: a Compose service, a container run by hand, a systemd unit or a cron job. */
export interface Unit {
  key: string;
  kind: 'container' | 'service' | 'timer' | 'cron';
  /** Its name inside its deployment, which a forced type is keyed by. */
  brick: string;
  /** systemd or cron: the rule that takes it (the start of its name or program). */
  host: { kind: 'systemd' | 'cron'; prefix: string } | null;
  /** The rule that takes it: its Compose project and service, else its name. */
  compose: { project: string; service: string } | null;
  containers: string[];
  image: string;
  /** Forced by the person; null keeps the type the portal guesses (the catalogue keeps corrections only). */
  type: 'service' | 'data' | 'task' | null;
  application: string | null;
  environment: string | null;
  domains: string[];
}

export interface DraftApplication { name: string; role: string; repository: string | null }

/** The word a name carries, the environment's name it suggests and the type of that environment (D48), a guess. */
const ENV_PATTERNS: [RegExp, string, string][] = [
  [/(^|[-_.])(pre-?prod|preproduction)([-_.]|$)/i, 'preprod', 'preproduction'],
  [/(^|[-_.])(staging|stage|stg)([-_.]|$)/i, 'staging', 'preproduction'],
  [/(^|[-_.])(recette|rec|test|qa)([-_.]|$)/i, 'recette', 'acceptance'],
  [/(^|[-_.])(dev|develop|development)([-_.]|$)/i, 'dev', 'development'],
  [/(^|[-_.])(prod|production|prd)([-_.]|$)/i, 'prod', 'production'],
];

/** « patrimy-production » → prod; null when the name says nothing. */
export function environmentOf(name: string): string | null {
  for (const [pattern, environment] of ENV_PATTERNS) if (pattern.test(name)) return environment;
  return null;
}

/** D48: the type a guessed environment name suggests (prod → production), null for another name. */
export function guessedEnvironmentType(name: string): string | null {
  return ENV_PATTERNS.find(([, environment]) => environment === name)?.[2] ?? null;
}

/** « patrimy-production » → patrimy: the name without its environment. */
export function baseOf(name: string): string {
  const base = ENV_PATTERNS.reduce((n, [pattern]) => n.replace(pattern, (_, before: string, _env: string, after: string) => (before && after ? before : '')), name);
  return slug(base) || slug(name);
}

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '').slice(0, 50);

const DATA = /(^|\/)(postgres|postgis|mysql|mariadb|mongo|redis|valkey|keydb|memcached|elasticsearch|opensearch|rabbitmq|minio|clickhouse|influxdb|timescale|cassandra)([:@/-]|$)/i;
const TASK = /(migrat|seed|init|setup|bootstrap|fixtures)/i;

/** The application a service seems to belong to and its role; databases and tasks go with the backend. */
const ROLE_WORDS = /^(front|frontend|web|ui|app|api|back|backend|admin|backoffice|worker|cron|scheduler)$/i;

function applicationOf(named: string, service: string, image: string): { name: string; role: string } {
  // Without Compose labels the base comes from the container's name (« patrimy-production-front »):
  // its role word is the suffix this function adds, never twice (patrimy-front, not patrimy-front-front).
  const words = named.split('-');
  const base = words.length > 1 && ROLE_WORDS.test(words.at(-1)!) ? words.slice(0, -1).join('-') : named;
  if (DATA.test(image) || TASK.test(service)) return { name: `${base}-api`, role: 'backend' };
  if (/admin|back-?office/i.test(service)) return { name: `${base}-admin`, role: 'admin-frontend' };
  if (/front|web|app|ui|nginx|next|nuxt/i.test(service)) return { name: `${base}-front`, role: 'frontend' };
  if (/worker|queue|celery|sidekiq|consumer/i.test(service)) return { name: `${base}-worker`, role: 'worker' };
  if (/cron|scheduler|beat/i.test(service)) return { name: `${base}-cron`, role: 'cron' };
  return { name: `${base}-api`, role: 'backend' };
}

const BRICK_WORDS = /^(postgres|postgresql|postgis|pg|db|database|mysql|mariadb|mongo|mongodb|redis|valkey|keydb|cache|memcached|elastic|elasticsearch|opensearch|rabbitmq|mq|minio|clickhouse|influxdb|migrate|migration|migrations|seed|init|setup)$/i;

/** « patrimy-postgres » → patrimy: the words that name a database, cache or task, not the application. */
export function withoutBrickWords(base: string): string {
  const kept = base.split('-').filter(word => !BRICK_WORDS.test(word));
  return kept.length ? kept.join('-') : base;
}

/** `https://github.com/kanbios/patrimy-api` → kanbios/patrimy-api, when it is of the organisation. */
export function repositoryOf(source: string | null | undefined, organisation: string): string | null {
  const match = source?.match(/github\.com[/:]([^/]+)\/([^/.\s]+)/i);
  return match && match[1]!.toLowerCase() === organisation.toLowerCase() ? `${match[1]}/${match[2]}` : null;
}

/**
 * The lines and applications the portal proposes for what no declared deployment takes yet.
 * `ignored` names the containers and units a group ignored on the server takes (D48 « Ignorer »).
 */
export function prefill(containers: Container[], sites: Site[], organisation: string, hostUnits: HostUnit[] = [], ignored: string[] = []): { units: Unit[]; applications: DraftApplication[] } {
  // What a declared deployment already takes, by a rule or through one of its sites, is not proposed again;
  // nor what a person chose to ignore.
  const skipped = new Set(ignored);
  const attached = sites.filter(s => s.attachedTo);
  const served = new Set(attached.flatMap(s => [...(s.destination?.container ? [s.destination.container] : []), ...s.containers]));
  const domains = new Set(attached.flatMap(s => s.domains));
  // The platform's own Caddy is the server's door, never an application to declare.
  const loose = containers.filter(c => !c.brick && !c.platform && !skipped.has(c.name) && !served.has(c.name) && !(c.site && domains.has(c.site)));
  const byKey = new Map<string, Unit>();
  const applications = new Map<string, DraftApplication>();
  for (const c of loose) {
    const compose = c.compose?.project && c.compose.service ? { project: c.compose.project, service: c.compose.service } : null;
    // Outside Compose a line is the container's name without its replica number: patrimy-production-front-1 and -2 are one.
    const key = compose ? `${compose.project}/${compose.service}` : c.name.replace(/-\d+$/, '');
    const existing = byKey.get(key);
    if (existing) { existing.containers.push(c.name); continue; }
    const scope = compose?.project ?? c.name;
    const named = baseOf(compose ? compose.project : c.name.replace(/[-_]\d+$/, ''));
    // A database, cache or task run on its own (« patrimy-postgres ») belongs to the backend of its project (D48):
    // its technical word is not part of the application's name.
    const base = DATA.test(c.image) || TASK.test(compose?.service ?? c.name) ? withoutBrickWords(named) : named;
    const app = applicationOf(base, compose?.service ?? c.name, c.image);
    const repository = repositoryOf(c.source, organisation);
    const known = applications.get(app.name);
    if (!known) applications.set(app.name, { ...app, repository });
    else if (!known.repository && repository) known.repository = repository;
    const own = sites.filter(s => s.destination?.container === c.name || s.containers.includes(c.name) || (c.site !== null && s.domains.includes(c.site))).flatMap(s => s.domains);
    byKey.set(key, { key, kind: 'container', brick: compose?.service ?? c.name, host: null, compose, containers: [c.name], image: c.image, type: null, application: app.name, environment: environmentOf(scope), domains: own });
  }
  // D48: the services, timers and cron jobs no deployment takes; tasks go with the backend.
  // A unit named after a Compose project's application goes with it (« patrimy-production-backup » → patrimy).
  const bases = new Set([...byKey.values()].flatMap(x => (x.compose ? [baseOf(x.compose.project)] : [])));
  for (const u of hostUnits.filter(x => !x.brick && !skipped.has(x.key))) {
    const name = u.kind === 'cron' ? u.name : u.name.replace(/\.(service|timer)$/, '');
    const cleaned = u.kind === 'cron' ? baseOf(programBase(u.name)) : baseOf(name);
    const base = [...bases].find(b => cleaned === b || cleaned.startsWith(`${b}-`)) ?? cleaned;
    const app = u.kind === 'service' ? applicationOf(base, name, '') : { name: `${base}-api`, role: 'backend' };
    if (!applications.has(app.name)) applications.set(app.name, { ...app, repository: null });
    byKey.set(u.key, {
      key: u.key, kind: u.kind, brick: u.kind === 'cron' ? u.name.split('/').pop() || u.name : name,
      host: { kind: u.kind === 'cron' ? 'cron' : 'systemd', prefix: u.name },
      compose: null, containers: [u.name], image: u.detail, type: null, application: app.name, environment: environmentOf(u.kind === 'cron' ? u.name.replace(/\//g, '-') : name), domains: [],
    });
  }
  return { units: [...byKey.values()], applications: [...applications.values()] };
}

/** `/opt/patrimy/bin/purge.sh` → patrimy: the first folder that names something, else the file. */
function programBase(path: string): string {
  const parts = path.split('/').filter(Boolean);
  const named = parts.slice(0, -1).find(p => !['opt', 'srv', 'var', 'www', 'home', 'usr', 'local', 'bin', 'sbin', 'scripts', 'app', 'apps', '.'].includes(p));
  return named ?? (parts.at(-1) ?? path).replace(/\.[a-z]+$/, '');
}

/**
 * D48 « Déclarer » on one detected application: its lines keep their guess,
 * every other line is left out (no application), so the wizard declares that group only.
 */
export function onlyGroup(guess: { units: Unit[]; applications: DraftApplication[] }, group: string | null): { units: Unit[]; applications: DraftApplication[] } {
  if (!group || !guess.units.some(u => u.application === group)) return guess;
  return {
    units: guess.units.map(u => (u.application === group ? u : { ...u, application: null })),
    applications: guess.applications.filter(a => a.name === group),
  };
}

/** The rules that take a set of lines: Compose projects and their services, else names; systemd and cron by their start. */
export function rulesOf(list: Unit[]): { containers: ContainerRule[]; units: UnitRule[] } {
  const containers: ContainerRule[] = [];
  const byProject = new Map<string, string[]>();
  for (const u of list) {
    if (u.host) continue;
    if (u.compose) byProject.set(u.compose.project, [...(byProject.get(u.compose.project) ?? []), u.compose.service]);
    // The rule keeps the hyphen before the replica number: it takes -1, -2…, not « …-frontend ».
    else containers.push({ kind: 'prefix', prefix: u.containers.some(n => /-\d+$/.test(n)) ? `${u.key}-` : u.key });
  }
  for (const [project, services] of byProject) containers.unshift({ kind: 'compose', project, services });
  return { containers, units: list.flatMap(u => (u.host ? [u.host] : [])) };
}

export interface DeploymentExtras { url: string | null; external: string; alsoUses: string }

/**
 * The deployments the lines make: one per application × environment, with the rules that take its containers.
 * D48: `typeOf` gives the type of each environment (the project's, or the one the person chose for a new one).
 */
export function deploymentsOf(
  units: Unit[], extras: Record<string, DeploymentExtras>, typeOf: (environment: string) => string | null = guessedEnvironmentType,
): NonNullable<DeclarationDraft['deployments']> {
  const groups = new Map<string, Unit[]>();
  for (const u of units) {
    if (!u.application || !u.environment) continue;
    const key = `${u.application}-${u.environment}`;
    groups.set(key, [...(groups.get(key) ?? []), u]);
  }
  return [...groups.entries()].map(([key, list]) => {
    const { containers, units } = rulesOf(list);
    const types = Object.fromEntries(list.filter(u => u.type).map(u => [u.brick, u.type!]));
    const extra = extras[key];
    return {
      application: list[0]!.application!,
      environment: list[0]!.environment!,
      ...(typeOf(list[0]!.environment!) ? { environmentType: typeOf(list[0]!.environment!)! } : {}),
      url: extra?.url ?? (list.flatMap(u => u.domains)[0] ? `https://${list.flatMap(u => u.domains)[0]}` : null),
      containers,
      types,
      external: parseExternal(extra?.external ?? ''),
      alsoUses: parseAlsoUses(extra?.alsoUses ?? ''),
      ...(units.length ? { units } : {}),
    };
  });
}

/** « base=data:tcp://host:5432 ; s3=data:https://… » as typed; the backend says which entry it cannot read. */
function parseExternal(value: string) {
  return value.split(';').map(s => s.trim()).filter(Boolean).map(entry => {
    const [name = '', rest = ''] = entry.split('=');
    const colon = rest.indexOf(':');
    return { name: name.trim(), type: rest.slice(0, colon).trim() as 'service' | 'data' | 'task', target: rest.slice(colon + 1).trim() };
  });
}

function parseAlsoUses(value: string) {
  return value.split(',').map(s => s.trim()).filter(Boolean).map(entry => {
    const [deployment = '', brick = ''] = entry.split('/');
    return { deployment: deployment.trim(), brick: brick.trim() };
  });
}
